import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {logOut, refreshAuthToken} from '../slices/globalActions';
import {finishLoading, startLoading, setServerErrorStatus} from '../slices/shared';
import {getIsAuthorized, getAccessToken, setAccessToken} from '../slices/authorization';
import {equal, pipe} from '../utils';
import {setCookie} from '../helpers/cookies';
import {HTTP_CODE_UNAUTHORIZED, HTTP_CODE_INTERNAL_SERVER_ERROR, API_BASE_URL} from '../constants';
import {CONSENT_ENDPOINT, SSO_ENDPOINT, CLIENT_ANALYSES_ENDPOINT, TAG_TYPES} from './constants';
import {CookieNames} from '../generalTypes';
import {
    GetConsentStatusParams,
    GetConsentStatusResponseData,
    AcceptConsentParams,
    AcceptConsentResponseData,
    ExchangeRefreshTokenParams,
    ExchangeRefreshTokenResponseData,
    RefreshTokenResponseData,
    GetUserResponseData,
    RefreshTokenParams,
    GetClientAnalysesResponseData
} from './types';

const globalExtraOptions = {
    isLoader: true
};

const prepareHeaders = (headers: Headers, {getState}: any) => {
    const state = getState();
    const accessToken = getAccessToken(state);

    if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return headers;
};

const baseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders
});

// @ts-ignore
const ensureTokenFreshness = async (args, api, extraOptions) => {
    const response = await baseQuery(args, api, extraOptions);
    const {error, meta} = response;
    const state = api.getState();
    const isAuthorized = getIsAuthorized(state);
    const isAuthorizationError = equal(error?.status, HTTP_CODE_UNAUTHORIZED);

    if (!isAuthorized || !isAuthorizationError) {
        return response;
    }

    const currentAccessToken = getAccessToken(state);

    if (equal(meta?.request.headers.get('Authorization'), `Bearer ${currentAccessToken}`)) {
        const {isSuccess, data} = await api.dispatch(refreshAuthToken());

        if (!isSuccess) {
            api.dispatch(logOut());

            return response;
        }

        const {token} = data as RefreshTokenResponseData;

        api.dispatch(setAccessToken(token));
        setCookie(CookieNames.accessToken, token);
    }

    const updatedResponse = await baseQuery(args, api, extraOptions);

    return updatedResponse;
};

// @ts-ignore
const handleServerError = async (response, api) => {
    const {dispatch} = api;
    const {error} = await response;
    const {status, originalStatus} = error ?? {};
    const errorCode = originalStatus || status;
    const isServerError = errorCode >= HTTP_CODE_INTERNAL_SERVER_ERROR;

    if (isServerError) {
        dispatch(setServerErrorStatus(errorCode));
    }

    return response;
};

// @ts-ignore
const enhancedBaseQuery = async (args, api, extraOptions) => {
    const updatedExtraOptions = {...globalExtraOptions, ...extraOptions};
    const {isLoader} = updatedExtraOptions;
    const enhancedRequest = pipe(
        ensureTokenFreshness,
        (response: any) => handleServerError(response, api)
    );

    if (isLoader) {
        api.dispatch(startLoading());
    }

    const response = await enhancedRequest(args, api, updatedExtraOptions);

    if (isLoader) {
        api.dispatch(finishLoading());
    }

    return response;
};

const api = createApi({
    reducerPath: 'api',
    baseQuery: enhancedBaseQuery,
    tagTypes: Object.values(TAG_TYPES),
    endpoints: builder => ({
        exchangeRefreshToken: builder.mutation<ExchangeRefreshTokenResponseData, ExchangeRefreshTokenParams>({
            query: data => ({
                url: `${SSO_ENDPOINT}/exchange`,
                method: 'POST',
                body: data
            })
        }),
        refreshToken: builder.mutation<RefreshTokenResponseData, RefreshTokenParams>({
            query: data => ({
                url: `${SSO_ENDPOINT}/refresh`,
                method: 'POST',
                body: data
            })
        }),
        getUser: builder.query<GetUserResponseData, void>({
            query: () => ({
                url: `${SSO_ENDPOINT}/me`,
                method: 'GET'
            })
        }),
        getConsentStatus: builder.query<GetConsentStatusResponseData, GetConsentStatusParams>({
            query: ({type}) => ({
                url: `${CONSENT_ENDPOINT}/status`,
                method: 'GET',
                params: {type}
            })
        }),
        acceptConsent: builder.mutation<AcceptConsentResponseData, AcceptConsentParams>({
            query: ({consentDocumentId}) => ({
                url: `${CONSENT_ENDPOINT}/accept`,
                method: 'POST',
                body: {consentDocumentId}
            })
        }),
        getClientAnalyses: builder.query<GetClientAnalysesResponseData, void>({
            query: () => ({
                url: CLIENT_ANALYSES_ENDPOINT,
                method: 'GET'
            }),
            providesTags: [TAG_TYPES.clientAnalyses]
        })
    })
});

export const {
    useGetConsentStatusQuery,
    useAcceptConsentMutation,
    useExchangeRefreshTokenMutation,
    useRefreshTokenMutation,
    useGetUserQuery,
    useLazyGetUserQuery,
    useGetClientAnalysesQuery,
    useLazyGetClientAnalysesQuery
} = api;
export {TAG_TYPES};
export default api;
